import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Car, Timer, AlertCircle, Loader2, Info, CheckCircle2, TrendingUp } from 'lucide-react';

/**
 * TimeEstimateDisplay Component
 * Shows live job time estimates during booking flow
 */
const TimeEstimateDisplay = ({ 
  estimate, 
  isLoading = false, 
  error = null,
  showBreakdown = true,
  compact = false 
}) => {
  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`
          bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl
          ${compact ? 'p-3' : 'p-4'}
        `}
      >
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-[var(--primary)] animate-spin" />
          <span className="text-[var(--text-secondary)]">Calculating estimated time...</span>
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`
          bg-red-500/10 border border-red-500/30 rounded-xl
          ${compact ? 'p-3' : 'p-4'}
        `}
      >
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-400">Unable to estimate time</span>
        </div>
      </motion.div>
    );
  }

  if (!estimate) {
    return null;
  }

  const confidenceColors = {
    high: 'text-green-500',
    medium: 'text-yellow-500',
    low: 'text-orange-500'
  };

  const confidenceLabels = {
    high: 'High confidence',
    medium: 'Medium confidence',
    low: 'Preliminary estimate'
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3 }}
        className={`
          bg-gradient-to-br from-[var(--background-paper)] to-[var(--background-subtle)]
          border border-[var(--border-gold)] rounded-xl overflow-hidden
          ${compact ? '' : 'shadow-lg'}
        `}
        data-testid="time-estimate-display"
      >
        {/* Header */}
        <div className="bg-[var(--primary)]/10 px-4 py-3 border-b border-[var(--border-gold)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Timer className="w-5 h-5 text-[var(--primary)]" />
              <h3 className="font-semibold text-[var(--text-primary)]">Estimated Job Time</h3>
            </div>
            {estimate.confidence && (
              <div className={`flex items-center gap-1 text-xs ${confidenceColors[estimate.confidence]}`}>
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>{confidenceLabels[estimate.confidence]}</span>
              </div>
            )}
          </div>
        </div>

        {/* Main Estimates */}
        <div className={`${compact ? 'p-3' : 'p-4'}`}>
          <div className={`grid ${compact ? 'grid-cols-3 gap-2' : 'grid-cols-3 gap-4'}`}>
            {/* Labor Time */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <Clock className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-blue-400`} />
                <span className={`text-[var(--text-muted)] ${compact ? 'text-xs' : 'text-sm'}`}>Labor</span>
              </div>
              <p className={`font-bold text-[var(--text-primary)] ${compact ? 'text-base' : 'text-lg'}`}>
                {estimate.labor_display || `${estimate.estimated_labor_hours || 1} hrs`}
              </p>
            </div>

            {/* Travel Time */}
            <div className="text-center border-x border-[var(--border-primary)]">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <Car className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-green-400`} />
                <span className={`text-[var(--text-muted)] ${compact ? 'text-xs' : 'text-sm'}`}>Travel</span>
              </div>
              <p className={`font-bold text-[var(--text-primary)] ${compact ? 'text-base' : 'text-lg'}`}>
                {estimate.travel_display || `${estimate.estimated_travel_minutes || 0} min`}
              </p>
            </div>

            {/* Total Time */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <TrendingUp className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-[var(--primary)]`} />
                <span className={`text-[var(--text-muted)] ${compact ? 'text-xs' : 'text-sm'}`}>Total</span>
              </div>
              <p className={`font-bold text-[var(--primary)] ${compact ? 'text-lg' : 'text-xl'}`}>
                {estimate.total_display || `${estimate.estimated_total_hours || 1.5} hrs`}
              </p>
            </div>
          </div>

          {/* Breakdown (optional) */}
          {showBreakdown && estimate.breakdown && !compact && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 pt-4 border-t border-[var(--border-primary)]"
            >
              <p className="text-xs text-[var(--text-muted)] mb-2 flex items-center gap-1">
                <Info className="w-3.5 h-3.5" />
                Time Breakdown
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {estimate.breakdown.base_time > 0 && (
                  <div className="flex justify-between text-[var(--text-secondary)]">
                    <span>Base time:</span>
                    <span>{estimate.breakdown.base_time} min</span>
                  </div>
                )}
                {estimate.breakdown.addon_time > 0 && (
                  <div className="flex justify-between text-[var(--text-secondary)]">
                    <span>Add-ons:</span>
                    <span>+{estimate.breakdown.addon_time} min</span>
                  </div>
                )}
                {estimate.breakdown.ai_adjustment > 0 && (
                  <div className="flex justify-between text-[var(--text-secondary)]">
                    <span>Complexity:</span>
                    <span>+{estimate.breakdown.ai_adjustment} min</span>
                  </div>
                )}
                {estimate.breakdown.travel_time > 0 && (
                  <div className="flex justify-between text-[var(--text-secondary)]">
                    <span>Travel:</span>
                    <span>{estimate.breakdown.travel_time} min</span>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* AI Factors */}
          {estimate.ai_analysis?.factors?.length > 0 && !compact && (
            <div className="mt-3 pt-3 border-t border-[var(--border-primary)]">
              <p className="text-xs text-[var(--text-muted)] mb-1">AI detected factors:</p>
              <div className="flex flex-wrap gap-1">
                {estimate.ai_analysis.factors.map((factor, idx) => (
                  <span
                    key={`ai-factor-${factor.slice(0,15)}-${idx}`}
                    className="px-2 py-0.5 bg-[var(--primary)]/10 text-[var(--primary)] text-xs rounded-full"
                  >
                    {factor}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className={`mt-3 pt-3 border-t border-[var(--border-primary)] ${compact ? 'hidden' : ''}`}>
            <p className="text-xs text-[var(--text-muted)] italic">
              This is an estimate. Actual completion time may vary based on job conditions.
            </p>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TimeEstimateDisplay;
